// Core
import { createSSRApp, defineAsyncComponent, h, onMounted, ref } from 'vue'
import { createInertiaApp, router } from '@inertiajs/vue3'
import Layout from '@/layouts/Layout.vue'
import { renderToString } from '@vue/server-renderer'
import { Link as InertiaLink } from '@inertiajs/vue3'

// Styles
import 'swiper/css'
import 'floating-vue/dist/style.css'
import '@/assets/styles/tailwind.css'
import '@/assets/styles/fonts.css'
import '@/assets/styles/transitions.css'
import '@/assets/styles/animations.css'
import '@/assets/styles/buttons.css'

// Components
// Dev-only. The static import pulled the panel's JSON editor (about 1.1 MB raw)
// into the shared chunk every visitor downloads. Vite replaces
// import.meta.env.DEV with a literal at build time, so the import below is
// dead code in production builds and Rollup drops it.
const TheInertiaDevTools = import.meta.env.DEV
  ? defineAsyncComponent(() => import('@/shared/components/TheInertiaDevTools.vue'))
  : null

// Libraries
import FloatingVue, { hideAllPoppers } from 'floating-vue'
import { VueRecaptchaPlugin } from 'vue-recaptcha/head'
import Notifications from '@kyvg/vue3-notification'
import InfiniteLoading from 'vue-infinite-loading'
import ActionCableVue from 'actioncable-vue'
import { createPinia } from 'pinia'
import { setupAxios } from '@/services/axios'
import { isMentorTour, syncMentorTour } from '@/modules/mentor-tour/demoMode'
import { Settings } from 'luxon'

// Utils
import { isTouchDevice } from '@/shared/utils/isTouchDevice.js'

export const renderInertiaApp = (page = null) => {
  const isRunningInBrowser = import.meta.env.SSR === false
  const isRunningInServer = import.meta.env.SSR === true

  const csrfToken = isRunningInBrowser
    ? document.head.querySelector('[name=csrf-token]')?.content
    : null

  setupAxios({ csrfToken })

  FloatingVue.options.themes.menu.popperTriggers = ['hover']

  // Closing popover on escape key press and on Inertia page change
  if (isRunningInBrowser) {
    document.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape') return
      hideAllPoppers()
    })
    router.on('success', hideAllPoppers)

    // Update document title on navigation
    router.on('success', (event) => {
      const pageTitle = event.detail.page.props?.shared?.pageTitle
      if (pageTitle) {
        document.title = pageTitle
      }
    })
  }

  Settings.defaultLocale = 'en'

  return createInertiaApp({
    progress: {
      // primary-500 (tailwind.config.js)
      color: '#006CFF',
    },
    defaults: {
      future: {
        useScriptElementForInitialPage: true,
      },
      visitOptions: (visit) => {
        // Disable the native view-transition for blog routes — the big centered hero
        // and grid layout don't morph cleanly with other pages (or between blog index
        // and show), so the browser falls back to a visually distracting crossfade.
        const dest = visit?.url?.pathname || ''
        const current = typeof window !== 'undefined' ? window.location.pathname : ''
        const blogRoute = /^\/blog(\/|$)/
        const isBlogVisit = blogRoute.test(dest) || blogRoute.test(current)
        return { viewTransition: !isBlogVisit }
      },
    },
    ...(isRunningInServer ? { render: renderToString, page } : {}),
    resolve: (name) => {
      let pages = import.meta.glob('./modules/**/*.page.vue')
      const importPage = pages[`./modules/${name}.page.vue`]

      if (!importPage) {
        throw new Error(
          `Unknown page ${name}. Is it located under ./modules with a .page.vue extension?`,
        )
      }

      return importPage().then((module) => {
        module.default.layout = module.default.layout === undefined ? Layout : module.default.layout

        // Body page color class has priority over layout color class
        const bodyClassMeta =
          module.default?.meta?.bodyClass ||
          module.default.layout?.meta?.bodyClass

        if (isRunningInBrowser) {
          document.body.classList.value = bodyClassMeta || 'bg-neutral-50'
        }

        return module.default
      })
    },
    setup({ el, App, props, plugin }) {
      // The mentor tour (/mentor-tour) turns on its demo mode before the
      // app starts, so no socket and no request opens for it.
      syncMentorTour(props.initialPage.props.shared?.mentorTour)
      if (isRunningInBrowser) {
        router.on('navigate', (event) => {
          syncMentorTour(event.detail.page.props.shared?.mentorTour)
        })
      }

      const pinia = createPinia()
      const app = createSSRApp({
        setup() {
          const showDevTools = ref(false)

          if (import.meta.env.DEV && isRunningInBrowser) {
            onMounted(() => {
              showDevTools.value = true
            })
          }

          return () => [
            h(App, props),
            showDevTools.value ? h(TheInertiaDevTools) : null,
          ]
        },
      })
        .component('InertiaLink', InertiaLink)
        .use(pinia)
        .use(FloatingVue, {
          themes: {
            tooltip: {
              triggers: isTouchDevice() ? ['click'] : ['hover', 'focus'],
              html: false,
            },
            dropdown: {
              html: false,
            },
          },
        })
        .use(VueRecaptchaPlugin, {
          v2SiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
        })
        .use(Notifications)
        .component('InfiniteLoading', {
          setup(props, { slots }) {
            return () =>
              h(InfiniteLoading, props, {
                spinner: () => slots.spinner(),
                'no-results': () =>
                  slots['no-results'] ? slots['no-results']() : '',
                'no-more': () => (slots['no-more'] ? slots['no-more']() : ''),
                error: () => 'Something went wrong while loading data.',
              })
          },
        })
        .use(ActionCableVue, {
          debug: true,
          debugLevel: 'error',
          connectionUrl: () => {
            if (import.meta.env.VITE_ACTION_CABLE_URL) {
              return import.meta.env.VITE_ACTION_CABLE_URL
            }
            const protocol =
              window.location.protocol === 'https:' ? 'wss:' : 'ws:'
            return `${protocol}//${window.location.host}/cable`
          },
          connectImmediately: isRunningInBrowser && !isMentorTour(),
        })
        .use(plugin)

      if (isRunningInBrowser) {
        import('@/services/appsignal').then(({ createVueErrorHandler }) => {
          app.config.errorHandler = createVueErrorHandler
        })
      }

      if (isRunningInServer) {
        return app
      } else {
        app.mount(el)
      }
    },
  })
}
